import React, { useState, useRef } from 'react';
import { Upload, X, Image, Video, Loader } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaUploader = ({ type = 'image', onUploadComplete, currentUrl = null }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const acceptedTypes = type === 'image' 
    ? 'image/jpeg,image/png,image/jpg,image/webp,image/gif'
    : 'video/mp4,video/webm,video/quicktime';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    setError(null);

    // Validate file type
    const validTypes = type === 'image'
      ? ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif']
      : ['video/mp4', 'video/webm', 'video/quicktime'];

    if (!validTypes.includes(file.type)) {
      setError(`Invalid file type. Please upload ${type === 'image' ? 'an image (JPG, PNG, WebP, GIF)' : 'a video (MP4, WebM)'}`);
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/announcements/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      const fullUrl = `${BACKEND_URL}${response.data.url}`;
      setPreview(fullUrl);
      
      if (onUploadComplete) {
        onUploadComplete({
          url: fullUrl,
          filename: response.data.filename,
          media_type: response.data.media_type
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload file');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onUploadComplete) {
      onUploadComplete({ url: null, filename: null, media_type: null });
    }
  };

  return (
    <div className="media-uploader">
      {!preview ? (
        <div
          className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '2px dashed #cbd5e1',
            borderRadius: '12px',
            padding: '2rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragActive ? '#f0f9ff' : '#f8fafc',
            transition: 'all 0.3s'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          
          {uploading ? (
            <div>
              <Loader className="animate-spin mx-auto mb-3" size={48} color="#3b82f6" />
              <p style={{ color: '#64748b' }}>Uploading...</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                {type === 'image' ? (
                  <Image size={48} color="#94a3b8" style={{ margin: '0 auto' }} />
                ) : (
                  <Video size={48} color="#94a3b8" style={{ margin: '0 auto' }} />
                )}
              </div>
              <p style={{ fontSize: '1rem', fontWeight: '600', color: '#334155', marginBottom: '0.5rem' }}>
                <Upload size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Drop {type} here or click to browse
              </p>
              <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                {type === 'image' ? 'JPG, PNG, WebP, GIF' : 'MP4, WebM, MOV'} • Max 10MB
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="media-preview" style={{ position: 'relative' }}>
          <button
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(239, 68, 68, 0.9)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10,
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(220, 38, 38, 1)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)'}
          >
            <X size={18} />
          </button>
          
          {type === 'image' ? (
            <img
              src={preview}
              alt="Preview"
              style={{
                width: '100%',
                maxHeight: '300px',
                objectFit: 'cover',
                borderRadius: '12px',
                border: '2px solid #e2e8f0'
              }}
            />
          ) : (
            <video
              src={preview}
              controls
              style={{
                width: '100%',
                maxHeight: '300px',
                borderRadius: '12px',
                border: '2px solid #e2e8f0'
              }}
            />
          )}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          fontSize: '0.875rem'
        }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
